import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table,
  TableBody,
  TableCell as CTableCell,
  TableHead as CTableHead,
  TableHeader,
  TableRow,
  TableLoading
} from "@/components/ui/table";
import { Route as ShiftHistoryRoute } from "@/routes/(inventory)/branch/_layout/reports/shift-history/index";
import {
  dateFormat,
  dayOfWeekFormat,
  hoursFormat,
} from "@/utils/time_format";
import { useNavigate } from "@tanstack/react-router";
import {
  CaretSortIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import { SetStateAction, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import { cn } from "@/lib/utils";
import PaginationButton from "@/components/ui/pagination-button";
import { useShiftHistoryData } from "../-hooks/useShiftHistoryData";

const TableCell = (props: React.ThHTMLAttributes<HTMLTableCellElement>) => {
  return <CTableCell className="text-center" {...props} />;
};
const TableHead = (props: React.ThHTMLAttributes<HTMLTableCellElement>) => {
  return <CTableHead className="text-center" {...props} />;
};

// TableCell and TableHead wrappers for consistent styling
const StyledTableCell = ({ className = "", ...props }) => (
  <CTableCell className={cn(tableCellClass, "py-2 font-medium text-center", className)} {...props} />
);
const StyledTableHead = ({ className = "", ...props }) => (
  <CTableHead className={cn(tableHeadClass, "text-center", className)} {...props} />
);

export default function Component() {
  const searchParams = ShiftHistoryRoute.useSearch();
  const navigate = useNavigate();
  
  const { data, isLoading, error, isFetching } = useShiftHistoryData();
  
  const shifts = data?.items || [];
  const totalCount = data?.totalCount || 0;

  const [order, setOrder] = useState<'asc' | 'desc'>("desc");
  const [orderBy, setOrderBy] = useState<string>("createdAt");

  const columns = [
    { key: "created_at", label: "Created At" },
    { key: "day", label: "Day" },
    { key: "user", label: "User" },
    { key: "register_id", label: "Register ID" },
    { key: "start_time", label: "Start Time" },
    { key: "end_time", label: "End Time" },
    { key: "hours", label: "Hours" },
    { key: "payouts", label: "Payouts" },
    { key: "cash_start", label: "Cash Start" },
    { key: "cash_end", label: "Cash End" },
    { key: "card_start", label: "Card Start" },
    { key: "card_end", label: "Card End" },
    { key: "auto_end", label: "Auto End?" },
  ];
  const visibleColumns = columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>);

  const handleRequestSort = (property: SetStateAction<string>) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = shifts.slice().sort((a, b) => {
    if (orderBy === "createdAt") {
      const dateA = dayjs(a.createdAt).valueOf();
      const dateB = dayjs(b.createdAt).valueOf();
      return order === "asc" ? dateA - dateB : dateB - dateA;
    }
    return 0;
  });

  const page = Number(searchParams.page) || 1;
  const pageSize = searchParams.pageSize || 10;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="overflow-y-scroll h-screen pb-48">
      <div className="flex items-center justify-between">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => {
                  navigate({
                    to: "/branch/reports",
                  });
                }}
                style={{
                  cursor: "pointer",
                }}
              >
                Reports Overview
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Shift Reports</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <hr className="my-4" />
      <div className="mt-4 border rounded-lg bg-white overflow-hidden">
        {isLoading ? (
          <TableLoading columns={columns} visibleColumns={visibleColumns} />
        ) : !data?.items || data.items.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-sm text-gray-500">No entries found</div>
          </div>
        ) : (
          <>
            <Table className="text-sm">
          <TableHeader className="group">
            <TableRow className="hover:bg-inherit cursor-pointer">
              <StyledTableHead
                rowSpan={2}
                onClick={() => handleRequestSort("createdAt")}
                style={{ cursor: "pointer" }}
              >
                <div className="flex items-center">
                  <span>Created At</span>
                  {orderBy === "createdAt" && (
                    <CaretSortIcon
                      className={`ml-2 h-4 w-4 ${order === "asc" ? "rotate-180" : ""}`}
                    />
                  )}
                </div>
              </StyledTableHead>
              <StyledTableHead rowSpan={2}>Day</StyledTableHead>
              <StyledTableHead rowSpan={2}>User</StyledTableHead>
              <StyledTableHead rowSpan={2}>Register ID</StyledTableHead>
              <StyledTableHead rowSpan={2}>Start Time</StyledTableHead>
              <StyledTableHead rowSpan={2}>End Time</StyledTableHead>
              <StyledTableHead rowSpan={2}>Hours</StyledTableHead>
              <StyledTableHead rowSpan={2}>Payouts</StyledTableHead>
              <StyledTableHead className="text-center" colSpan={2}>
                Cash Amount
              </StyledTableHead>
              <StyledTableHead className="text-center" colSpan={2}>
                Card Amount
              </StyledTableHead>
              <StyledTableHead rowSpan={2}>Auto End?</StyledTableHead>
            </TableRow>
            <TableRow className="hover:bg-inherit">
              <StyledTableHead>Start</StyledTableHead>
              <StyledTableHead>End</StyledTableHead>
              <StyledTableHead>Start</StyledTableHead>
              <StyledTableHead>End</StyledTableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.map((row: any, index: number) => {
              const isLastRow = index === sortedData.length - 1;
              const handleRowClick = () => {
                navigate({
                  to: `/reports/shift-history/shift-summary/$shiftId`,
                  params: { shiftId: row.shiftId },
                });
              };
              return (
                <TableRow
                  key={row.shiftId}
                  onClick={handleRowClick}
                  className="cursor-pointer"
                >
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>
                    {dateFormat(row.createdAt)}
                  </StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{dayOfWeekFormat(row.createdAt)}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{row.createdBy?.firstName} {row.createdBy?.lastName}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{row.details?.cashRegisterId ?? "-"}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>
                    {row.startTime && row.startTime !== "0001-01-01T00:00:00Z" && dayjs(row.startTime).isValid()
                      ? dayjs(row.startTime).format("HH:mm")
                      : "-"}
                  </StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>
                    {row.endTime && dayjs(row.endTime).isValid()
                      ? dayjs(row.endTime).format("HH:mm")
                      : "-"}
                  </StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{hoursFormat(row.calculatedTotalHours ?? 0)}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{row.details?.payouts ?? "-"}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{row.details?.cashDrawer?.startAmount ?? "-"}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{row.details?.cashDrawer?.endAmount ?? "-"}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{row.details?.card?.startAmount ?? "-"}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{row.details?.card?.endAmount ?? "-"}</StyledTableCell>
                  <StyledTableCell className={isLastRow ? tableCellLastRowClass : ""}>{row.systemEnded ? "Yes" : "No"}</StyledTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </>
        )}
        <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
        <p>{data?.items && data.items.length > 0 ? totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
          <div className="flex items-center gap-1">
            <PaginationButton
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    page: 1
                  }
                });
              }}
              disabled={page === 1}
            >
              <DoubleArrowLeftIcon className="h-4 w-4" />
            </PaginationButton>
            <PaginationButton
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    page: page - 1
                  }
                });
              }}
              disabled={page === 1}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </PaginationButton>
            {(() => {
              const getPageNumbers = () => {
                const pages = [];
                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) {
                    pages.push(i);
                  }
                } else {
                  pages.push(1);
                  if (page > 3) pages.push('...');
                  for (let i = Math.max(2, page - 1); i <= Math.min(page + 1, totalPages - 1); i++) {
                    pages.push(i);
                  }
                  if (page < totalPages - 2) pages.push('...');
                  pages.push(totalPages);
                }
                return pages;
              };
              return getPageNumbers().map((p, idx) => (
                p === '...'
                  ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                  : <PaginationButton
                      key={p}
                      active={page === p}
                      onClick={() => {
                        navigate({
                          search: {
                            ...searchParams,
                            page: typeof p === 'number' ? p : page
                          }
                        });
                      }}
                    >
                      <span className="text-sm">{p}</span>
                    </PaginationButton>
              ));
            })()}
            <PaginationButton
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    page: page + 1
                  }
                });
              }}
              disabled={page >= totalPages}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </PaginationButton>
            <PaginationButton
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    page: totalPages
                  }
                });
              }}
              disabled={page >= totalPages}
            >
              <DoubleArrowRightIcon className="h-4 w-4" />
            </PaginationButton>
          </div>
        </div>
      </div>
    </div>
  );
}
